<template>
  <div class="list-container">
    <div class="list-header">
      <h1>Users Admin</h1>
      <router-link to="/admin/users/new" class="important-button big">
        <font-awesome-icon icon="plus" /> New User
      </router-link>
    </div>

    <div class="stats-panel">
      <div class="stats-container">
        <div class="stat-box">
          <div class="stat-title">TU</div>
          <div class="stat-value">{{ users.length }}</div>
          <div class="stat-description">Total Users</div>
        </div>
        <div 
          class="stat-box clickable" 
          :class="{ 'active': activeFilter === 'mau' }"
          @click="toggleFilter('mau')"
        >
          <div class="stat-title">MAU</div>
          <div class="stat-value">{{ monthlyActiveUsers }}</div>
          <div class="stat-description">Monthly Active Users</div>
        </div>
        <div 
          class="stat-box clickable" 
          :class="{ 'active': activeFilter === 'wau' }"
          @click="toggleFilter('wau')"
        >
          <div class="stat-title">WAU</div>
          <div class="stat-value">{{ weeklyActiveUsers }}</div>
          <div class="stat-description">Weekly Active Users</div>
        </div>
        <div 
          class="stat-box clickable" 
          :class="{ 'active': activeFilter === 'mua' }"
          @click="toggleFilter('mua')"
        >
          <div class="stat-title">MUA</div>
          <div class="stat-value">{{ monthlyUserAcquisition }}</div>
          <div class="stat-description">Monthly User Acquisition</div>
        </div>
        <div 
          class="stat-box clickable" 
          :class="{ 'active': activeFilter === 'wua' }"
          @click="toggleFilter('wua')"
        >
          <div class="stat-title">WUA</div>
          <div class="stat-value">{{ weeklyUserAcquisition }}</div>
          <div class="stat-description">Weekly User Acquisition</div>
        </div>
      </div>
      <div v-if="activeFilter" class="filter-indicator">
        <span>Filtering by: {{ getFilterName(activeFilter) }}</span>
        <button class="clear-filter" @click="clearFilter">Clear Filter</button>
      </div>
    </div>

    <LoadingIndicator v-if="isLoading">Loading users...</LoadingIndicator>
    <table v-else>
      <thead>
        <tr>
          <SortableListHeader
            column="name" 
            :current-sort="sortColumn" 
            default-direction="asc"
            @sort="handleSort"
          >
            Name
          </SortableListHeader>
          <SortableListHeader
            column="email" 
            :current-sort="sortColumn" 
            default-direction="asc"
            @sort="handleSort"
          >
            Email
          </SortableListHeader>
          <SortableListHeader 
            column="verified" 
            :current-sort="sortColumn" 
            @sort="handleSort"
          >
            Verified
          </SortableListHeader>
          <SortableListHeader 
            column="organizations" 
            :current-sort="sortColumn" 
            @sort="handleSort"
          >
            Organizations
          </SortableListHeader>
          <SortableListHeader 
            column="registered" 
            :current-sort="sortColumn" 
            @sort="handleSort"
          >
            Registered
          </SortableListHeader>
          <SortableListHeader 
            column="lastOnline" 
            :current-sort="sortColumn" 
            @sort="handleSort"
          >
            Last Online
          </SortableListHeader>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="sortedUsers.length === 0">
          <td colspan="7">No users found.</td>
        </tr>
        <tr 
          v-for="user in sortedUsers" 
          :key="user.id" 
          @click="handleRowClick($event, user)" 
          class="clickable"
          :title="`View &quot;${user.first_name} ${user.last_name}&quot;`"
        >
          <td class="overflow">
            <b :title="`${user.first_name} ${user.last_name}`">
              {{ user.first_name }} {{ user.last_name }}
            </b>
          </td>
          <td class="overflow">
            <a :href="`mailto:${user.email}`" :title="user.email">
              {{ user.email }}
            </a>
          </td>
          <td>
            <span v-if="user.verified_email">Yes</span>
            <span v-else>No</span>
          </td>
          <td>
            <div v-if="user.organizations && user.organizations.length > 0">
              <template v-for="(org, index) in user.organizations" :key="org.id">
                <router-link 
                  :to="`/organizations/${org.id}`" 
                  :title="`View Organization &quot;${org.name}&quot; (${org.member_roles.join(', ')})`"
                  class="link-button"
                >
                  <RoleBadges :roles="org.member_roles" :maxBadges=1 />
                  {{ org.name }}
                </router-link>
                <br v-if="index < user.organizations.length - 1">
              </template>
            </div>
            <span v-else>-</span>
          </td>
          <td :title="formatDatetime(user.created_at)">{{ formatDate(user.created_at) }}</td>
          <td>{{ user.last_online ? formatDatetime(user.last_online) : 'Never' }}</td>
          <td>
            <span class="actions">
              <router-link
                :to="`/users/${user.id}/edit`"
                class="action-button edit"
                title="Edit"
              >
                <font-awesome-icon icon="edit" />
              </router-link>
              <button
                @click="confirmDelete(user)"
                class="action-button delete"
                title="Delete"
              >
                <font-awesome-icon icon="trash" />
              </button>
            </span>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import { getAllUsersWithOrganizations, deleteUser, userKeys } from '@/scripts/api/userApi';
import { FontAwesomeIcon } from '@/scripts/icons';
import { handleApiError, isInteractiveElement, formatDate, formatDatetime, showConfirmDialog } from '@/scripts/shared-utils';
import SortableListHeader from '@/components/SortableListHeader.vue';
import RoleBadges from '@/components/RoleBadges.vue';
import LoadingIndicator from '@/components/LoadingIndicator.vue';

const router = useRouter();
const queryClient = useQueryClient();

const { data: users, isLoading } = useQuery({
  queryKey: userKeys.getAllUsersWithOrganizations(),
  queryFn: getAllUsersWithOrganizations,
  staleTime: 60000,
  placeholderData: [],
  onError: (error) => handleApiError(error, 'fetching users'),
});

const activeFilter = ref(null);
const sortColumn = ref('lastOnline');
const sortDirection = ref('desc');

const monthlyActiveUsers = computed(() => {
  if (!users.value) return 0;
  return users.value.filter(isMonthlyActiveUser).length;
});

const weeklyActiveUsers = computed(() => {
  if (!users.value) return 0;
  return users.value.filter(isWeeklyActiveUser).length;
});

const monthlyUserAcquisition = computed(() => {
  if (!users.value) return 0;
  return users.value.filter(isMonthlyAcquiredUser).length;
});

const weeklyUserAcquisition = computed(() => {
  if (!users.value) return 0;
  return users.value.filter(isWeeklyAcquiredUser).length;
});

const filteredUsers = computed(() => {
  if (!users.value) return [];
  if (!activeFilter.value) return users.value;
  
  const filterMap = {
    'mau': isMonthlyActiveUser,
    'wau': isWeeklyActiveUser,
    'mua': isMonthlyAcquiredUser,
    'wua': isWeeklyAcquiredUser,
  };

  return users.value.filter(filterMap[activeFilter.value]);
});

const sortedUsers = computed(() => {
  let sortedUsers = filteredUsers.value ? [...filteredUsers.value] : [];
  
  sortedUsers.sort((a, b) => {
    let comparison = 0;
    let lhs, rhs;

    switch (sortColumn.value) {
      case 'name':
        lhs = `${a.first_name} ${a.last_name}` || '';
        rhs = `${b.first_name} ${b.last_name}` || '';
        comparison = lhs.localeCompare(rhs);
        break;
        
      case 'email':
        lhs = a.email || '';
        rhs = b.email || '';
        comparison = lhs.localeCompare(rhs);
        break;
        
      case 'verified':
        lhs = a.verified_email ? 1 : 0;
        rhs = b.verified_email ? 1 : 0;
        comparison = lhs - rhs;
        break;
        
      case 'organizations':
        lhs = a.organizations ? a.organizations.length : 0;
        rhs = b.organizations ? b.organizations.length : 0;
        comparison = lhs - rhs;
        break;
        
      case 'registered':
        lhs = new Date(a.created_at || 0).getTime();
        rhs = new Date(b.created_at || 0).getTime();
        comparison = lhs - rhs;
        break;
        
      case 'lastOnline':
      default:
        lhs = a.last_online ? new Date(a.last_online).getTime() : 0;
        rhs = b.last_online ? new Date(b.last_online).getTime() : 0;
        comparison = lhs - rhs;
        break;
    }
    
    return sortDirection.value === 'asc' ? comparison : -comparison;
  });

  return sortedUsers;
});

const handleSort = (sortData) => {
  sortColumn.value = sortData.column;
  sortDirection.value = sortData.direction;
};

const getTimeThresholds = () => {
  const now = new Date();
  const monthAgo = new Date(now);
  monthAgo.setDate(now.getDate() - 28); // 28 days for monthly metrics
  
  const weekAgo = new Date(now);
  weekAgo.setDate(now.getDate() - 7); // 7 days for weekly metrics
  
  return { monthAgo, weekAgo };
};

const isMonthlyActiveUser = (user) => new Date(user.last_online) >= getTimeThresholds().monthAgo;
const isWeeklyActiveUser = (user) => new Date(user.last_online) >= getTimeThresholds().weekAgo;
const isMonthlyAcquiredUser = (user) => new Date(user.created_at) >= getTimeThresholds().monthAgo;
const isWeeklyAcquiredUser = (user) => new Date(user.created_at) >= getTimeThresholds().weekAgo;

const toggleFilter = (filter) => {
  activeFilter.value = activeFilter.value === filter ? null : filter;
};

const clearFilter = () => {
  activeFilter.value = null;
};

const getFilterName = (filter) => {
  return {
    mau: 'Monthly Active Users',
    wau: 'Weekly Active Users',
    mua: 'Monthly User Acquisition',
    wua: 'Weekly User Acquisition',
  }[filter] || '';
};

const confirmDelete = async (user) => {
  const confirmed = showConfirmDialog(`Are you sure you want to delete the user "${user.first_name} ${user.last_name}"?`);
  if (confirmed) {
    try {
      await deleteUser(user.id);
      // Invalidate the query to refetch the user list automatically
      await queryClient.invalidateQueries({ queryKey: userKeys.getAllUsers() });
      await queryClient.invalidateQueries({ queryKey: userKeys.getAllUsersWithOrganizations() });
    } catch (error) {
      handleApiError(error, 'deleting user');
    }
  }
};

const handleRowClick = (event, user) => {
  if (!isInteractiveElement(event)) {
    router.push(`/users/${user.id}`);
  }
};
</script>

<style scoped>
@import '@/styles/shared-list.css';
@import '@/styles/organization-members.css';
@import '@/styles/shared-stats.css';
</style>
